import { useEffect } from "react";
import { useStoreApi } from "@xyflow/react";
import { useCanvasStore } from "@/store/useCanvasStore";

/** Poll interval while waiting for React Flow to commit a node element. */
const RETRY_MS = 50;
/** ~2s of retries, then give up — an id whose node was deleted never arrives. */
const MAX_ATTEMPTS = 40;

/**
 * Re-measures elements that arrived over the WebSocket.
 *
 * Chrome suspends the rendering pipeline for hidden/occluded tabs: no
 * requestAnimationFrame, and — the part that bites — no ResizeObserver callback
 * delivery. React Flow measures every node through a shared ResizeObserver, so a
 * node appended while the tab is hidden keeps `measured: {}` and
 * `handleBounds: null`. RF then silently skips any edge whose endpoint is
 * unmeasured, and the edge never mounts even though the store holds it.
 *
 * Two details decide whether this actually works, both learned the hard way on
 * a real hidden tab:
 *
 *  1. It calls the RF store's `updateNodeInternals` directly rather than the
 *     public `useUpdateNodeInternals()` hook. That hook defers its work inside
 *     `requestAnimationFrame` (@xyflow/react 12.11.0), which never fires while
 *     the tab is hidden — precisely the condition being repaired. The direct
 *     call is synchronous and reads offsetWidth/offsetHeight, which background
 *     tabs still compute. `triggerFitView: false` matches the hook, so healing
 *     never yanks the camera.
 *
 *  2. It retries on a timer. RF commits its node elements a render after the
 *     store learns about them, so the first pass usually finds nothing; without
 *     a retry the id stayed queued forever. The retry must be a timer, not rAF,
 *     for the same reason as above.
 *
 * Must render inside <ReactFlow> so useStoreApi resolves. Renders nothing.
 *
 * See internal-docs/edge-added-render-fix.md.
 */
export default function MeasureHealer() {
  const rfStore = useStoreApi();
  const pendingMeasureIds = useCanvasStore((s) => s.pendingMeasureIds);
  const clearPendingMeasureIds = useCanvasStore((s) => s.clearPendingMeasureIds);

  useEffect(() => {
    if (pendingMeasureIds.length === 0) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const drain = () => {
      const { domNode, updateNodeInternals } = rfStore.getState();
      const scope: ParentNode = domNode ?? document;

      // RF types nodeElement as HTMLDivElement — that is what it renders nodes as.
      const updates = new Map<string, { id: string; nodeElement: HTMLDivElement; force: boolean }>();
      for (const id of pendingMeasureIds) {
        const nodeElement = scope.querySelector<HTMLDivElement>(
          `.react-flow__node[data-id="${CSS.escape(id)}"]`,
        );
        if (nodeElement) updates.set(id, { id, nodeElement, force: true });
      }

      if (updates.size > 0) {
        updateNodeInternals(updates, { triggerFitView: false });
        // Ids still missing stay queued: clearing only what we healed re-runs
        // this effect, which retries them with a fresh budget.
        clearPendingMeasureIds([...updates.keys()]);
        return;
      }

      attempts += 1;
      if (attempts < MAX_ATTEMPTS) timer = setTimeout(drain, RETRY_MS);
    };

    drain();
    return () => clearTimeout(timer);
  }, [pendingMeasureIds, rfStore, clearPendingMeasureIds]);

  return null;
}
